import React, { FunctionComponent, useMemo, useRef } from 'react';
import { Select } from 'View/Common';
import { useGetInsuranceCompaniesQuery } from 'api/graphql';
import { LoadingSpinner } from 'View/Common';
import { isMobile } from 'react-device-detect';
import { HEADER_OFFSET } from 'utils/mobileScrolls';

export interface IHealthInsuranceEntry {
  __typename?: 'InsuranceCompany';
  name: string;
  reductionRate: number;
  cooperationUrl?: string | null;
  cooperationPartner?: boolean | null;
  cooperationLogo?: string | null;
}

interface IProps {
  onChange?: (healthInsuranceEntry: IHealthInsuranceEntry) => void;
  className?: string;
  style?: React.CSSProperties;
}

type TSelectOption = { value: string; label: string; entry: IHealthInsuranceEntry };
type TCooperationObject = { cooperations: TSelectOption[]; others: TSelectOption[] };

// --- robuste Scroll-Lock Utilities ---
const useScrollLock = () => {
  const preventScrollRef = useRef<((e: TouchEvent) => void) | null>(null);

  const lockAt = (originalY: number, visibleY: number) => {
    const body = document.body;
    const html = document.documentElement;

    // ursprüngliche Position merken (Dataset-Eigenschaft setzen, Objekt NICHT überschreiben!)
    body.dataset.scrollY = String(originalY);

    // Body fixieren und so verschieben, dass das Select oben im Viewport sichtbar ist
    body.style.position = 'fixed';
    body.style.top = `-${visibleY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden'; // iOS-Safari-Helfer
    body.classList.add('dropdown-open');

    // Touch-Scrolls unterbinden (iOS benötigt preventDefault + passive:false)
    if (!preventScrollRef.current) {
      preventScrollRef.current = (e: TouchEvent) => {
        e.preventDefault();
      };
      window.addEventListener('touchmove', preventScrollRef.current, { passive: false });
    }
  };

  const unlock = () => {
    const body = document.body;
    const html = document.documentElement;

    const stored = body.dataset.scrollY;
    const y = stored ? parseInt(stored, 10) : 0;

    if (preventScrollRef.current) {
      window.removeEventListener('touchmove', preventScrollRef.current as EventListener);
      preventScrollRef.current = null;
    }

    body.style.position = '';
    body.style.top = '';
    body.style.width = '';
    body.style.overflow = '';
    html.style.overflow = '';
    body.classList.remove('dropdown-open');

    delete body.dataset.scrollY;

    // ursprüngliche Position wiederherstellen
    window.scrollTo(0, y);
  };

  return { lockAt, unlock };
};

export const HealthInsuranceSelect: FunctionComponent<IProps> = ({ className, style, onChange }) => {
  const { loading, data } = useGetInsuranceCompaniesQuery();
  const { insuranceCompanies } = data || {};
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { lockAt, unlock } = useScrollLock();

  const orderedInsuranceCompanies: TCooperationObject = React.useMemo(() => {
    if (!insuranceCompanies) {
      // eslint-disable-next-line
      return { cooperations: [], others: [] };
    }
    return [...insuranceCompanies]
      .sort((a, b) => a.name.localeCompare(b.name))
      .reduce(
        (acc: TCooperationObject, entry: IHealthInsuranceEntry) => {
          if (entry.cooperationPartner) {
            acc.cooperations.push({ value: `cooperations-${acc.cooperations.length}`, label: entry.name, entry });
          } else {
            acc.others.push({ value: `others-${acc.others.length}`, label: entry.name, entry });
          }
          return acc;
        },
        //eslint-disable-next-line
        { cooperations: [], others: [] },
      );
  }, [insuranceCompanies]);

  const handleOnChange = (value: string) => {
    const [type, index] = value.split('-') as [keyof TCooperationObject, number];
    if (!insuranceCompanies || orderedInsuranceCompanies[type].length <= Number(index)) return;
    onChange && onChange(orderedInsuranceCompanies[type][index].entry);
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    // Nur Mobile: Body muss gesperrt werden, solange Select offen ist
    if (!isMobile) return;

    if (open) {
      const originalY = window.pageYOffset;

      // Position des Select-Wrappers bestimmen und Ziel berechnen
      const rect = wrapperRef.current?.getBoundingClientRect();
      const targetY = rect ? rect.top + originalY - HEADER_OFFSET : originalY;

      // Sofort locken und Viewport so setzen, dass Select oben steht
      // (ohne Delay und ohne Smooth, damit kein „Zwischenscrollen“ möglich ist)
      lockAt(originalY, targetY);
    } else {
      // Lock lösen und ursprüngliche Position herstellen
      unlock();
    }
  };

  if (loading || !insuranceCompanies) {
    return <LoadingSpinner />;
  }

  const { cooperations, others } = orderedInsuranceCompanies;

  return (
    <div ref={wrapperRef} className={className} style={style}>
      <Select
        onChange={handleOnChange}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        // Wichtig: Dropdown im Wrapper rendern, damit Fixed-Body keine Fehlposition verursacht
        getPopupContainer={() => wrapperRef.current || document.body}
        options={
          !!cooperations.length
            ? [
                { label: 'Krankenkassen ohne Vorauszahlung', options: cooperations },
                { label: 'Alle Krankenkassen', options: others },
              ]
            : others
        }
        placeholder={'Wähle deine Krankenkasse'}
      />
    </div>
  );
};
